import { enqueueSnackbar } from 'notistack';
import { all, any, equals, isNil } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { createAction } from 'redux-actions';

import { resetForm, setValue } from '@gef-ui/form-core/stream/transfer/control';
import { getMessage } from '@gef-ui/localization/messages';

import fetch from 'features/common/fetch';
import { getContextUser } from 'features/views/userContext/selectors';

import * as types from './actionTypes';
import { emailNotificationIdsMapToFields } from './constants';
import { SETTINGS_EMAILS_FORM_NAME } from './form';
import {
	getCheckedFilterState,
	getMainSwitchValueByNotificationSetId,
	getSubfiltersOfFilter,
	getSubSwitchByNotifIdAndTaskType,
} from './selectors';

export const reset = createAction(types.RESET_REDUCER);
export const changeTab = createAction(types.CHANGE_TAB, (tab) => ({ tab }));
export const changeAllNotifsByNotificationSetId = createAction(
	types.CHANGE_ALL_NOTIFS,
	(notificationSetId, originalValue) => ({ notificationSetId, originalValue }),
);
export const changeSubNotifByNotifIdAndTaskTypeId = createAction(
	types.CHANGE_SUB_NOTIF,
	(notificationSetId, taskTypeId) => ({ notificationSetId, taskTypeId }),
);

export const setUserNotificationSettings = createAction(types.SET_USER_NOTIFS, (notificationSettings) => ({
	notificationSettings,
}));

export const setTaskFilters = createAction(types.SET_TASK_FILTERS, (taskFilters) => ({ taskFilters }));

export const fetchNotificationSettings =
	({ resetEmailForm }) =>
	(dispatch, getState, { sendControlNext }) => {
		const contextUser = getContextUser(getState());
		resetEmailForm && sendControlNext(SETTINGS_EMAILS_FORM_NAME, resetForm());

		dispatch(
			fetch.actions.get(fetch.constants.urls.userNotificationSettings, {
				additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
				onSuccess: [
					(response) => () => {
						if (!response.body) return;
						const { notificationFilters = [] } = response.body;
						dispatch(setUserNotificationSettings(notificationFilters));
					},
				],
			}),
		);
	};

export const updateNotifEmailByNotificationId =
	(notificationSetId) =>
	async (dispatch, getState, { getFormData, sendControlNext }) => {
		const contextUser = getContextUser(getState());
		const formData = await getFormData(SETTINGS_EMAILS_FORM_NAME);
		const fieldName = emailNotificationIdsMapToFields[notificationSetId];

		dispatch(
			fetch.actions.put(fetch.constants.urls.userNotificationSettingsEmail, {
				body: {
					email: formData[fieldName],
					notificationSetId,
					userLogin: contextUser?.login ?? null,
				},
				onSuccess: [
					() => () => {
						sendControlNext(SETTINGS_EMAILS_FORM_NAME, setValue({ fieldName, value: formData[fieldName] }));
					},
				],
			}),
		);
	};

export const changeMainSwitchValueByNotificationSetId = (notificationSetId) => (dispatch, getState) => {
	const contextUser = getContextUser(getState());

	const originalValue = getMainSwitchValueByNotificationSetId(getState(), notificationSetId);

	dispatch(
		fetch.actions.put(fetch.constants.urls.userNotificationSettings, {
			body: {
				enabled: !originalValue,
				notificationSetId,
				userLogin: contextUser?.login ?? null,
			},
			onSuccess: [
				() => () => {
					dispatch(changeAllNotifsByNotificationSetId(notificationSetId, originalValue));
				},
			],
		}),
	);
};

export const changeSubSwitchValueByNotifIdAndTaskTypeId = (notificationSetId, taskTypeId) => (dispatch, getState) => {
	const contextUser = getContextUser(getState());

	const chosenSubSwitch = getSubSwitchByNotifIdAndTaskType(getState(), notificationSetId, taskTypeId);

	dispatch(
		fetch.actions.put(fetch.constants.urls.userNotificationSettings, {
			body: {
				enabled: !chosenSubSwitch.enabled,
				notificationSetId,
				taskTypeId,
				userLogin: contextUser?.login ?? null,
			},
			onSuccess: [
				() => () => {
					dispatch(changeSubNotifByNotifIdAndTaskTypeId(notificationSetId, taskTypeId));
				},
			],
		}),
	);
};

export const changeAllSubSwitchesByNotifIdAndTaskTypeId = (items, enabled) => (dispatch, getState) => {
	const contextUser = getContextUser(getState());

	const itemsAllowedToToggle = items.filter((item) => {
		const chosenSubSwitch = getSubSwitchByNotifIdAndTaskType(getState(), item?.notificationSetId, item?.taskTypeId);
		return chosenSubSwitch.allowedChanges;
	});

	const promises = itemsAllowedToToggle.map(async (item) => {
		const { notificationSetId, taskTypeId } = item;
		await dispatch(
			fetch.actions.put(fetch.constants.urls.userNotificationSettings, {
				body: {
					enabled,
					notificationSetId,
					taskTypeId,
					userLogin: contextUser?.login ?? null,
				},
			}),
		);
	});

	return Promise.all([...promises]).then(() => {
		dispatch(fetchNotificationSettings({ resetEmailForm: false }));
	});
};

export const fetchTaskFilters = () => (dispatch, getState) => {
	const contextUser = getContextUser(getState());

	dispatch(
		fetch.actions.get(fetch.constants.urls.userFilters, {
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
			onSuccess: [
				(response) => () => {
					if (!response.body) return;
					const { taskFilters } = response.body;
					const formatedTaskFilters = {};

					taskFilters.forEach((filter) => {
						const { taskTypeId, taskSubtypeId } = filter;

						if (isNil(taskSubtypeId)) {
							formatedTaskFilters[taskTypeId] = filter;
						}

						if (!isNil(taskSubtypeId)) {
							if (!formatedTaskFilters[taskTypeId]) {
								formatedTaskFilters[taskTypeId] = filter;
								formatedTaskFilters[taskTypeId].subTypes = [filter];
							} else {
								if (!formatedTaskFilters[taskTypeId].subTypes) {
									formatedTaskFilters[taskTypeId].subTypes = [filter];
								} else {
									formatedTaskFilters[taskTypeId].subTypes.push(filter);
								}
							}
						}
					});

					const formatedTaskFiltersArray = Object.values(formatedTaskFilters);
					dispatch(setTaskFilters(formatedTaskFiltersArray));
				},
			],
		}),
	);
};

const changeSubfilterChecked = createAction(types.CHANGE_SUBFILTER_CHECKED, (taskTypeId, filterId) => ({
	taskTypeId,
	filterId,
}));

const changeFilterCheckedWithoutSubfilter = createAction(
	types.CHANGE_FILTER_CHECKED_WITHOUT_SUBFILTER,
	(taskTypeId) => ({
		taskTypeId,
	}),
);

export const changeSubfilter = (taskTypeId, filterId) => (dispatch, getState) => {
	dispatch(changeSubfilterChecked(taskTypeId, filterId));

	const subfiltersOfFilter = getSubfiltersOfFilter(getState(), taskTypeId);

	const subfiltersStatesArr = subfiltersOfFilter && subfiltersOfFilter.map((filter) => filter.enabled);
	const filterState = getCheckedFilterState(getState(), taskTypeId);

	if (!filterState && any(equals(true))(subfiltersStatesArr || [])) {
		dispatch(changeFilterCheckedWithoutSubfilter(taskTypeId));
	}

	if (filterState && all(equals(false))(subfiltersStatesArr || [])) {
		dispatch(changeFilterCheckedWithoutSubfilter(taskTypeId));
	}
};

export const saveSetting = (filter) => (dispatch, getState) => {
	const contextUser = getContextUser(getState());

	const body = {
		id: filter?.id,
		enabled: !filter?.enabled,
		taskTypeId: filter?.taskTypeId,
	};

	if (!isNilOrEmpty(filter?.taskSubtypeId)) {
		body.taskSubtypeId = filter?.taskSubtypeId;
	}

	if (contextUser) {
		body.userLogin = contextUser?.login;
	}

	dispatch(
		fetch.actions.post(fetch.constants.urls.userFilters, {
			body,
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
			onSuccess: [
				() => () => {
					if (!isNilOrEmpty(filter?.taskTypeId) && !isNilOrEmpty(filter?.taskSubtypeId)) {
						dispatch(changeSubfilter(filter?.taskTypeId, filter?.taskSubtypeId));
					} else {
						dispatch(changeFilter(filter?.taskTypeId));
					}
					enqueueSnackbar(getMessage('toastr:approveSaveUserFilters'), { variant: 'success' });
				},
			],
			onError: [
				(e) => () => {
					enqueueSnackbar(getMessage('toastr:errorSaveUserFilters'), { variant: 'error' });
					console.warn(e);
				},
			],
		}),
	);
};

const changeFilterChecked = createAction(types.CHANGE_FILTER_CHECKED, (taskTypeId) => ({ taskTypeId }));

export const changeFilter = (taskTypeId) => (dispatch) => {
	dispatch(changeFilterChecked(taskTypeId));
};
